import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Injector, OnInit, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ICallableObject, IRequestOptions } from '@df/api/request/request.service';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Status } from '@df/core/status';
import { GtmService } from '@df/module-gtm/gtm.service';
import { NUgcImage } from '@df/ugc-image/ugc-image.namespace';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { AbstractFormComponent } from '@df/ui/form/abstract-form.component';
import { IFileInputEvent } from '@df/ui/form/file-input.component.def';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { Product } from '../../../catalog/product/product.decorator';
import { ActionComponent } from '../../../ui/api/action.component';
import { ResultComponent } from '../../../ui/api/result.component';
import { CheckboxComponent } from '../../../ui/form/checkbox';
import { FileInputComponent } from '../../../ui/form/file-input/file-input.component';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { NModalLayoutRight } from '../../../ui/modal/modal-layout-right.component';
import { MuiInputDirective } from '../../legacy/forms/mui-input.directive';

declare module 'git_modules/@tomandco/dragonfly-ugc-image/src/ugc-image.namespace' {
    namespace NUgcImage {
        interface IUploadData {
            nyw_item_range_code_nh?: number | undefined;
            item_in_photo?: string | undefined;
        }

        interface IContent {
            title: string;
            copy: string;
        }
    }
}

@DfCmsModal({
    id: 'ugcImageForm',
    name: 'UGC Image Form CMS Modal',
    position: 'right',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: NModalLayoutRight.getWidth(sizeName)
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: ['S', 'M', 'L', 'X']
})
@ICmsComponent<UgcImageFormComponent>({
    id: 'ugcImageForm',
    block: {
        url: 'ugcImageForm'
    },
    name: 'UGC Image Form CMS Modal',
    description: 'UGC Image Form CMS Modal',
    fields: [
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'copy',
            type: NICmsContentField.EFieldType.text,
            title: 'Copy',
            required: true
        }
    ]
})
@Component({
    selector: 'ugc-image-form',
    templateUrl: './ugc-image-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        FormsModule,
        ResultComponent,
        ActionComponent,
        ICmsClientModule,
        TranslationModule,
        CheckboxComponent,
        InputWrapComponent,
        MuiInputDirective,
        FileInputComponent,
        IfSizeDirective,
        InputWrapBodyDirective,
        SizeClassDirective
    ]
})
export class UgcImageFormComponent extends AbstractFormComponent<NUgcImage.IUploadData> implements OnInit {
    @ICmsContent({ type: 'blockContent' })
    content?: NUgcImage.IContent;

    readonly injector: any;
    fileStatus = new Status();
    consent;
    isSuccess = false;
    protected submitPath = NUgcImage.ENDPOINTS.upload;
    protected override requestOptions: IRequestOptions = { fileUpload: true };
    constructor(
        ref: ChangeDetectorRef,
        @Optional()
        private modalRef: ModalRef<UgcImageFormComponent>,
        @Optional()
        @Inject(MODAL_DATA)
        private product: Product<IProductDataV3>,
        private gtmService: GtmService,
        @Inject(Injector) injector: Injector
    ) {
        super(ref);
        this.injector = injector;
    }

    get filename(): string | undefined {
        return this.data?.image?.name;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.fileStatus.subscribe(() => this.detectChanges()));
    }

    onFilechange(event: IFileInputEvent): void {
        this.data.image = event.file;
        if (event.error?.message) {
            this.fileStatus.error = event.error?.message;
        } else {
            this.fileStatus.reset();
        }
        this.detectChanges();
    }

    close() {
        this.modalRef.close();
    }

    protected override getSubmitAction(): ICallableObject<Partial<NUgcImage.IUploadData>> {
        const superAction = super.getSubmitAction() as ICallableObject<Partial<NUgcImage.IUploadData>>;
        if (this.productSku && superAction.data) {
            superAction.data.sku = this.productSku;
            superAction.data.nyw_item_range_code_nh = this.product.data?.nyw_item_range_code_nh;
        }
        return superAction;
    }

    protected override onSubmitSuccess(response?: any): void {
        super.onSubmitSuccess(response);
        this.isSuccess = true;
        this.gtmService.sendEventGA4('upload_image', { item_name: this.product.data?.name, item_id: this.productSku });
        this.detectChanges();
    }

    private get productSku(): string | undefined {
        return this.product?.parent?.data?.sku;
    }
}
